import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";

import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import ViewContent from "../../../components/ViewContent";
import DatePickerField from "../../../components/DatePickerField";
import { fetchData } from "../../../fetchData";
import { formatDate } from "../../../utils";
import { stringAvatar } from "../../../utils/stringAvatar";
import { organizationsProfileLabels } from "../../../utils/labels";
import { navigateToOrganization } from "../../../utils/navigateToOrganization";

const productList = [
  { value: "essencial", label: "Essencial" },
  { value: "saveRH", label: "Save RH" },
  { value: "saveFinanc", label: "Save BPO Financeiro" },
];

const allProductValues = productList.map((p) => p.value);

const productLabel = (code: string) =>
  productList.find((o) => o.value === code)?.label ?? code;

const ratingColor = (rating: number) => {
  switch (rating) {
    case 1:
      return "#FC495B";
    case 2:
      return "#FF8352";
    case 3:
      return "#FFAA4D";
    case 4:
      return "#AFEF41";
    case 5:
      return "#45E330";
    default:
      return "#999";
  }
};

const averageColor = (avg: number) => {
  if (avg <= 1.5) return "#FC495B";
  if (avg <= 2.5) return "#FF8352";
  if (avg <= 3.5) return "#FFAA4D";
  if (avg <= 4.5) return "#AFEF41";
  return "#45E330";
};

const ratingLabel = (r: number) => {
  const labels: Record<number, string> = {
    1: "Muito difícil",
    2: "Difícil",
    3: "Neutro",
    4: "Fácil",
    5: "Muito fácil",
  };
  return labels[r] ?? String(r);
};

const defaultDistribution = [1, 2, 3, 4, 5].map((r) => ({
  rating: r,
  name: ratingLabel(r),
  total: 0,
  fill: ratingColor(r),
}));

const CesScoreCircle = ({ value }: { value: number }) => {
  const color = value > 0 ? averageColor(value) : "#999";
  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        width: 200,
        height: 200,
        borderRadius: 200,
        borderWidth: 7,
        borderStyle: "solid",
        borderColor: color,
        mx: "auto",
      }}
    >
      <Typography
        variant="h2"
        fontWeight={500}
        sx={{
          color,
        }}
      >
        {value > 0 ? value.toFixed(1).replace(".", ",") : "0,0"}
      </Typography>
      <Typography variant="caption">MÉDIA CES</Typography>
    </Stack>
  );
};

const CustomXAxisTick = ({ x, y, payload }: any) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={0}
      y={0}
      dy={10}
      textAnchor="end"
      fontSize={11}
      fill="#888"
      transform="rotate(-35)"
    >
      {payload.value}
    </text>
  </g>
);

const CesProducts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [report, setReport] = useState<any>(null);
  const [chartData, setChartData] = useState(defaultDistribution);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [derivedRows, setDerivedRows] = useState<any[]>([]);

  const profileRefsCache = useRef<Record<string, Set<number>>>({});
  const profileMapCache = useRef<Record<string, Record<number, string>>>({});

  const { register, handleSubmit, getValues, setValue } = useForm({
    defaultValues: {
      start: dayjs().startOf("year").toISOString(),
      end: dayjs().toISOString(),
    },
  });

  const preloadProfileData = async () => {
    if (Object.keys(profileRefsCache.current).length > 0) return;
    await Promise.all(
      organizationsProfileLabels.map(async (p) => {
        const res = await fetchData(
          `/organizations?profile=${encodeURIComponent(p)}&limit=9999&skip=0`,
          { method: "GET" },
        );
        const localMap: Record<number, string> = {};
        const localSet = new Set<number>();
        if (res?.rows) {
          res.rows.forEach((org: any) => {
            if (org.reference !== undefined && org.reference !== null) {
              localSet.add(org.reference);
              localMap[org.reference] = org.profile;
            }
          });
        }
        profileRefsCache.current[p] = localSet;
        profileMapCache.current[p] = localMap;
      }),
    );
  };

  const buildAllowedRefs = (profiles: string[]) => {
    const refs = new Set<number>();
    profiles.forEach((p) => {
      if (profileRefsCache.current[p]) {
        profileRefsCache.current[p].forEach((r) => refs.add(r));
      }
    });
    return refs;
  };

  const recalcDerived = () => {
    if (!report) return;
    setIsFiltering(true);
    try {
      let rows = report.rows || [];

      if (
        selectedProducts.length > 0 &&
        selectedProducts.length < allProductValues.length
      ) {
        rows = rows.filter((r: any) => selectedProducts.includes(r.product));
      }

      if (
        selectedProfiles.length > 0 &&
        selectedProfiles.length < organizationsProfileLabels.length
      ) {
        const allowedRefs = buildAllowedRefs(selectedProfiles);
        rows = rows.filter(
          (r: any) =>
            r.client?.reference && allowedRefs.has(r.client.reference),
        );
      }

      setDerivedRows(rows);

      const dist = [1, 2, 3, 4, 5].map((rating) => ({
        rating,
        name: ratingLabel(rating),
        total: rows.filter((r: any) => r.rating === rating).length,
        fill: ratingColor(rating),
      }));
      setChartData(dist);
    } finally {
      setIsFiltering(false);
    }
  };

  useEffect(() => {
    recalcDerived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, selectedProfiles, selectedProducts]);

  const getReport = async (custom?: { start: string; end: string }) => {
    setLoading(true);
    setError(false);
    try {
      const dates = custom ?? {
        start: getValues("start"),
        end: getValues("end"),
      };
      const payload = { product: "all", filter: "custom", custom: dates };
      const response = await fetchData(`/surveys/ces-products/report`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (response) {
        setReport(response);
        await preloadProfileData();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    getReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProductToggle = (value: string) => {
    setSelectedProducts((prev) => {
      if (prev.includes(value)) {
        return prev.filter((p) => p !== value);
      }
      return [...prev, value];
    });
  };

  const handleProfileToggle = (value: string) => {
    setSelectedProfiles((prev) => {
      if (prev.includes(value)) {
        return prev.filter((p) => p !== value);
      }
      return [...prev, value];
    });
  };

  async function onSubmit(data: any) {
    getReport({ start: data.start, end: data.end });
  }

  const total = derivedRows.length;
  const average =
    total > 0
      ? Math.round(
          (derivedRows.reduce((sum, r) => sum + r.rating, 0) / total) * 10,
        ) / 10
      : 0;

  const profileCounts = derivedRows.reduce(
    (acc: Record<string, number>, row: any) => {
      const profile = row.client?.profile;
      if (profile) acc[profile] = (acc[profile] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const showProductColumn = selectedProducts.length !== 1;

  const exportXlsx = async () => {
    const rows = derivedRows || [];
    const flatRows = rows.map((row: any) => ({
      referencia: row.client?.reference || "-",
      organizacao: row.client?.name || "-",
      produto: productLabel(row.product),
      carteira: row.client?.profile || "-",
      contato: row.user?.name || "-",
      nota: row.rating ?? "-",
      data: row.created_at ? formatDate(row.created_at, "D/M/Y H:i") : "-",
    }));

    const [{ default: XLSX }, { default: FileSaver }] = await Promise.all([
      import("sheetjs-style"),
      import("file-saver"),
    ]);

    const ws = XLSX.utils.json_to_sheet(flatRows);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] } as any;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    const baseName = "ces-produtos";
    FileSaver.saveAs(file, `${baseName}.xlsx`);
  };

  return (
    <ViewContent
      title="CES Produtos"
      icon={<BarChartOutlinedIcon sx={{ mr: 2 }} />}
      button={
        !loading && (
          <Button
            variant="contained"
            size="small"
            color="secondary"
            onClick={exportXlsx}
          >
            Exportar XLSX
          </Button>
        )
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Ops! Ocorreu um erro ao carregar o relatório. Tente novamente mais
          tarde.
        </Alert>
      )}

      {!error && (
        <div>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading || isFiltering}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={4} sx={{ display: "flex" }}>
              <Card sx={{ flex: 1 }}>
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Produto
                    </Typography>
                    <ToggleButtonGroup
                      orientation="vertical"
                      color="primary"
                      value={selectedProducts}
                      size="small"
                      fullWidth
                    >
                      {productList.map((opt) => (
                        <ToggleButton
                          key={opt.value}
                          value={opt.value}
                          onClick={() => handleProductToggle(opt.value)}
                        >
                          {opt.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Carteiras
                    </Typography>
                    <ToggleButtonGroup
                      orientation="vertical"
                      color="primary"
                      value={selectedProfiles}
                      size="small"
                      fullWidth
                    >
                      {organizationsProfileLabels.map((opt) => (
                        <ToggleButton
                          key={opt}
                          value={opt}
                          onClick={() => handleProfileToggle(opt)}
                        >
                          {opt}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Período
                    </Typography>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <Box flex={1}>
                          <DatePickerField
                            name="start"
                            register={register}
                            get={getValues}
                            set={setValue}
                            label="De"
                          />
                        </Box>
                        <Box flex={1}>
                          <DatePickerField
                            name="end"
                            register={register}
                            get={getValues}
                            set={setValue}
                            label="Até"
                          />
                        </Box>
                      </Stack>
                      <Button
                        variant="contained"
                        type="submit"
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Consultar
                      </Button>
                    </form>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Stack spacing={2} height="100%">
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Card sx={{ flex: 1 }}>
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <CesScoreCircle value={average} />
                    </CardContent>
                  </Card>

                  <Card sx={{ flex: 1 }}>
                    <CardContent
                      sx={{
                        textAlign: "center",
                        justifyContent: "center",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Total de respostas
                      </Typography>
                      <Typography variant="h2" fontWeight={600}>
                        {report ? total : "–"}
                      </Typography>
                      {report &&
                        total > 0 &&
                        Object.keys(profileCounts).length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              justifyContent: "center",
                            }}
                          >
                            {Object.entries(profileCounts).map(
                              ([profile, count]) => (
                                <Chip
                                  key={profile}
                                  label={`${profile}: ${count}`}
                                  size="small"
                                />
                              ),
                            )}
                          </Box>
                        )}
                    </CardContent>
                  </Card>
                </Stack>

                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Distribuição por nota
                    </Typography>
                    <Box width="100%" height={270}>
                      <ResponsiveContainer>
                        <BarChart
                          data={chartData}
                          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            tick={<CustomXAxisTick />}
                            height={75}
                            label={{
                              value: "Nota",
                              position: "insideBottom",
                              offset: 0,
                              style: { fill: "#888", fontSize: 12 },
                            }}
                          />
                          <YAxis
                            allowDecimals={false}
                            width={60}
                            label={{
                              value: "Respostas",
                              angle: -90,
                              position: "insideLeft",
                              offset: 10,
                              style: {
                                textAnchor: "middle",
                                fill: "#888",
                                fontSize: 12,
                              },
                            }}
                          />
                          <Tooltip
                            formatter={(value) => [value, "Respostas"]}
                          />
                          <Bar dataKey="total">
                            {chartData.map((row, index) => (
                              <Cell key={`cell-${index}`} fill={row.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          {report && derivedRows.length > 0 && (
            <>
              <Divider sx={{ my: 3 }}>
                <Chip label="RESPOSTAS" />
              </Divider>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Contato</TableCell>
                      <TableCell>Organização</TableCell>
                      {showProductColumn && <TableCell>Produto</TableCell>}
                      <TableCell>Carteira</TableCell>
                      <TableCell>Nota</TableCell>
                      <TableCell>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {derivedRows.map((row: any) => (
                      <TableRow
                        key={String(row._id)}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            {row.user?.name && (
                              <Avatar {...stringAvatar(row.user.name)} />
                            )}
                            <Typography variant="body2">
                              {row.user?.name || "–"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {row.client?.reference ? (
                            <Chip
                              label={row.client.reference}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={() =>
                                navigateToOrganization(
                                  row.client.reference,
                                  navigate,
                                )
                              }
                            />
                          ) : (
                            "–"
                          )}
                        </TableCell>
                        {showProductColumn && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {productLabel(row.product)}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.client?.profile || "–"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              backgroundColor: ratingColor(row.rating),
                              color: "common.white",
                              fontWeight: 700,
                              fontSize: "0.95rem",
                            }}
                          >
                            {row.rating}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {row.created_at
                            ? formatDate(row.created_at, "D/M/Y")
                            : "–"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {report && derivedRows.length === 0 && !loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 4 }}
            >
              Nenhuma resposta encontrada para os filtros selecionados.
            </Typography>
          )}
        </div>
      )}
    </ViewContent>
  );
};

export default CesProducts;
